import type { ImageResizeServiceOptions } from "./ImageResizeService.types"

type FileRef<T> = WritableComputedRef<T> | Ref<T>
export class ImageResizeService {
  constructor(
    private readonly options: ImageResizeServiceOptions,
    private readonly imageResizer: IImageResizer,
  ) {}

  isValidImage = (file: File) => file.type.startsWith("image/")

  /**
   * Resizes all provided image files according to the specified options. Non-image files are skipped.
   * @param files The image files to be resized
   * @param options The options to use for resizing the images
   * @returns Returns the resized versions of the provided image files.
   */
  async *resizeAll(
    files: FileRef<Array<File> | undefined>,
    options?: ImageResizeServiceOptions,
  ): AsyncGenerator<{ index: number; file: File; success: boolean }> {
    if (!files.value || files.value.length === 0) return

    for (let i = 0; i < files.value.length; i++) {
      try {
        const file = files.value[i]
        if (!file || !this.isValidImage(file)) continue

        const resizedFile = await this.resize(file, options)
        files.value[i] = resizedFile

        yield {
          index: i,
          file: resizedFile,
          success: true,
        }
      } catch (error) {
        console.error("Error resizing image at index", i, ":", error)

        yield {
          index: i,
          file: files.value[i]!,
          success: false,
        }
      }
    }
  }

  async resize(file: File, options?: ImageResizeServiceOptions): Promise<File> {
    try {
      // Merge the default image options with the provided options
      const resolvedOptions = {
        ...this.options,
        ...options,
      } as ImageResizeServiceOptions

      // Load the image from the provided file
      const loadedImage = await this.loadImage(file)

      // Calculate the target size for the image based on the resolved options
      const { width, height } = this.calculateTargetSize(loadedImage, resolvedOptions.imageSize)

      // Resize the image using the resizer with the calculated target size
      // for each resizer
      const resizedFile = await this.imageResizer.resize(
        file,
        {
          type: resolvedOptions.type,
          width,
          height,
        },
        loadedImage,
      )

      // Return the resized file
      return resizedFile ?? file
    } catch (error) {
      console.error("Error resizing image:", error)
      return file
    }
  }

  /**
   * Replaces the original image files with the resized ones
   * @param files The original image files
   * @param resizedImages The resized image files
   * @returns An array of files where each original file is replaced with its resized version if available
   */
  replaceWithResized(files: File[], resizedImages: File[]) {
    return files?.map((file) => {
      const resized = resizedImages.find((resizedFile) => this.normalizeFilename(resizedFile.name) === this.normalizeFilename(file.name))
      return resized ?? file
    })
  }

  private normalizeFilename = (name: string) => name.replace(/\.[^/.]+$/, "")

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      try {
        const image = new Image()
        const url = URL.createObjectURL(file)

        image.onload = () => {
          URL.revokeObjectURL(url)
          resolve(image)
        }

        image.onerror = (error) => {
          URL.revokeObjectURL(url)
          reject(error)
        }

        image.src = url
      } catch (error) {
        reject(error)
      }
    })
  }

  private calculateTargetSize(image: HTMLImageElement, imageSize: number) {
    const scale = Math.min(imageSize / image.naturalWidth, imageSize / image.naturalHeight, 1)

    return {
      width: Math.round(image.naturalWidth * scale),
      height: Math.round(image.naturalHeight * scale),
    }
  }
}
